import { useState, useEffect } from 'react'
import styles from './EditProjectModal.module.css'
import apiClient from '../../utils/api'
import useEscapeKey from '../../hooks/useEscapeKey'

export default function EditProjectModal({ 
    isOpen, 
    onClose, 
    onSave, 
    project, 
    apiToken,
    clients = []
}) {
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        color: '#3B82F6',
        client_id: '',
        status: 'active'
    })
    const [isLoading, setIsLoading] = useState(false)
    const [error, setError] = useState('')

    // Handle escape key to close modal (disabled when loading)
    useEscapeKey(isOpen, onClose, !isLoading)

    useEffect(() => {
        if (project && isOpen) {
            setFormData({
                name: project.name || '',
                description: project.description || '',
                color: project.color || '#3B82F6',
                client_id: project.client_id || '',
                status: project.status || 'active'
            })
            setError('')
        }
    }, [project, isOpen, clients])

    if (!isOpen) return null

    const handleSubmit = async (e) => {
        e.preventDefault()
        setIsLoading(true)
        setError('')

        try {
            apiClient.setAuthToken(apiToken)
            const response = await apiClient.request(`/projects/${project.id}`, {
                method: 'PUT',
                body: JSON.stringify({
                    project: {
                        name: formData.name.trim(),
                        description: formData.description.trim(),
                        color: formData.color,
                        client_id: formData.client_id || null,
                        status: formData.status
                    }
                })
            })

            if (response.success || response.project) {
                const updatedProject = response.project || response
                onSave(updatedProject, 'Project updated successfully')
                onClose()
            } else {
                setError(response.error || 'Failed to update project')
            }
        } catch (err) {
            console.error('Failed to update project:', err)
            setError(err.message || 'Failed to update project. Please try again.')
        } finally {
            setIsLoading(false)
        }
    }

    const handleChange = (e) => {
        const { name, value } = e.target
        setFormData(prev => ({
            ...prev,
            [name]: value
        }))
    }

    // Predefined color options - match CreateProjectModal
    const colorOptions = [
        '#3B82F6', // Blue
        '#10B981', // Green
        '#F59E0B', // Yellow
        '#EF4444', // Red
        '#8B5CF6', // Purple
        '#F97316', // Orange
        '#06B6D4', // Cyan
        '#84CC16', // Lime
        '#EC4899', // Pink
        '#6B7280'  // Gray
    ]

    return (
        <div className={styles.modalOverlay}>
            <div className={styles.modalContent}>
                <div className={styles.modalHeader}>
                    <h2>Edit Project</h2>
                    <button 
                        className={styles.closeButton}
                        onClick={onClose}
                        aria-label="Close modal"
                    >
                        ×
                    </button>
                </div>

                <form onSubmit={handleSubmit} className={styles.form}>
                    {error && (
                        <div className={styles.errorMessage}>
                            {error}
                        </div>
                    )}

                    <div className={styles.formRow}>
                        <div className={styles.formGroup}>
                            <label htmlFor="name">Project Name *</label>
                            <input
                                type="text"
                                id="name"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                placeholder="Enter project name"
                                required
                                disabled={isLoading}
                                maxLength={100}
                            />
                        </div>
                    </div>

                    <div className={styles.formRow}>
                        <div className={styles.formGroup}>
                            <label htmlFor="description">Description</label>
                            <textarea
                                id="description"
                                name="description"
                                value={formData.description}
                                onChange={handleChange}
                                placeholder="Project description (optional)"
                                disabled={isLoading}
                                rows="3"
                                maxLength={500}
                            />
                        </div>
                    </div>

                    <div className={styles.formRow}>
                        <div className={styles.formGroup}>
                            <label htmlFor="client_id">Client</label>
                            <select
                                id="client_id"
                                name="client_id"
                                value={formData.client_id}
                                onChange={handleChange}
                                disabled={isLoading}
                            >
                                <option value="">No Client</option>
                                {(clients || []).map(client => (
                                    <option key={client.id} value={client.id}>
                                        {client.name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className={styles.formGroup}>
                            <label htmlFor="status">Status</label>
                            <select
                                id="status"
                                name="status"
                                value={formData.status}
                                onChange={handleChange}
                                disabled={isLoading}
                                required
                            >
                                <option value="active">Active</option>
                                <option value="inactive">Inactive</option>
                                <option value="archived">Archived</option>
                            </select>
                        </div>
                    </div>

                    <div className={styles.formRow}>
                        <div className={styles.formGroup}>
                            <label htmlFor="color">Project Color *</label>
                            <div className={styles.colorSection}>
                                <input
                                    type="color"
                                    id="color"
                                    name="color"
                                    className={styles.colorInput}
                                    value={formData.color}
                                    onChange={handleChange}
                                    disabled={isLoading}
                                />
                                <div className={styles.colorOptions}>
                                    {colorOptions.map(color => (
                                        <button
                                            key={color}
                                            type="button"
                                            className={`${styles.colorOption} ${
                                                formData.color === color ? styles.colorOptionSelected : ''
                                            }`}
                                            style={{ backgroundColor: color }}
                                            onClick={() => handleChange({ target: { name: 'color', value: color } })}
                                            disabled={isLoading}
                                            aria-label={`Select color ${color}`}
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className={styles.modalActions}>
                        <button 
                            type="button" 
                            className={styles.cancelButton}
                            onClick={onClose}
                            disabled={isLoading}
                        >
                            Cancel
                        </button>
                        <button 
                            type="submit" 
                            className={styles.saveButton}
                            disabled={isLoading || !formData.name.trim()}
                        >
                            {isLoading ? 'Updating...' : 'Update Project'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    )
}